import React from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { MapPin, X } from 'lucide-react';

import { StatusBadge } from 'components/pages/certificates/StatusBadge';
import { font } from 'settings/tokens';
import { stateCardSurfaceSx } from './stateCardSurface';

interface SuspiciousActivityCardProps {
  /**
   * Where the invalid scan happened, already resolved to a human place by
   * `getScanLocationLabel`. Null when the scan carries no location.
   */
  place: string | null;
  /** Opens the Scans screen, focused on the offending scan where possible. */
  onReviewScan: () => void;
  /** Per-visit dismissal — see the note on the control below. */
  onDismiss: () => void;
}

/**
 * VERA-315 — "Suspicious activity detected", as a STATE CARD.
 *
 * ## What it replaces, and why the previous shape was wrong
 *
 * This was a bespoke MUI `Alert` (`severity='error'`, tinted outline) rendered
 * ABOVE the product title, i.e. above the name of the thing it is about and above
 * every other state the certificate can be in. Nothing chose that hierarchy; it was
 * added last and there was space at the top. The result was that the page's LEAST
 * certain signal — an invalid scan is evidence, not a confirmed state — outranked
 * a certificate the owner had actually reported stolen.
 *
 * It is now a member of the state-card family, ranked by `StateCardStack` below the
 * missing-state card and above a pending transfer. See `STATE_SEVERITY` for why
 * that order is the one `scanStatus.ts` already argues for.
 *
 * ## The card is neutral; the chip carries status
 *
 * Inherited wholesale from `MissingStateCallout`, whose own history records that a
 * status TINT and a left ACCENT BAR were each built and rejected on sight. The
 * previous Alert was a third instance of that same idea (a red-tinted fill plus a
 * red border), and it lost for the same reason plus one more: at the top of the
 * page it was the first thing the eye landed on, on a surface whose whole job is to
 * read as calm and authoritative.
 *
 * The signal is not weakened. It is carried by the chip here, by the standing
 * "N invalid" badge on the Scans row (which does NOT dismiss), and by the red pins
 * on the map itself.
 *
 * ## The chip says "Invalid scan", not "Suspicious"
 *
 * The heading already says suspicious. The chip's job is the specific fact, and
 * "Invalid scan" is the same word the Scans row's standing badge uses ("1 invalid")
 * and the same word the scan chips and map legend use — one vocabulary for one
 * event. `status='stolen'` is passed purely for the red ramp, matching what the
 * Scans-row badge already does; `StatusBadge` documents that a caller from another
 * domain supplies its own label rather than forcing its vocabulary through the
 * certificate-status maps.
 */
export const SuspiciousActivityCard = ({
  place,
  onReviewScan,
  onDismiss,
}: SuspiciousActivityCardProps) => (
  <Box sx={stateCardSurfaceSx}>
    <Stack spacing={1.5}>
      {/* Heading left, chip + dismiss RIGHT — the same space-between row
          `MissingStateCallout` uses, so the two cards read as one family. */}
      <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
        {/* `font.body` / 500 — the state-card family's one heading treatment. It
            spent part of 2026-08-30 at `font.heading` / 600 and was reverted with its
            three siblings; the reasoning lives in `tranferSummary.tsx` beside the
            card that prompted both changes. Never change this alone. */}
        <Typography sx={{ fontSize: font.body, fontWeight: 500 }}>
          Suspicious activity detected
        </Typography>
        <Stack direction='row' alignItems='center' spacing={0.5} sx={{ flexShrink: 0 }}>
          <StatusBadge status='stolen' label='Invalid scan' />
          {/*
            DISMISS SURVIVES THE MOVE, deliberately.

            The previous Alert had one and it would have been easy to drop on the
            grounds that no other state card dismisses. That reasoning is backwards:
            the other two cards describe states the owner can RESOLVE (report found,
            cancel the gift), so they retire themselves. This one describes something
            already in the past that the owner cannot undo — working-conventions
            calls out that suspicious activity is not actionable beyond being
            notified — so dismissal is the only way to put it down.

            Per visit, NOT persisted, which is the original rule and still right:
            on the next open the certificate is still flagged, and silencing a
            possible-counterfeit warning permanently from one tap is the wrong
            default. The standing badge on the Scans row does not dismiss at all.

            `X`, not a trash glyph: nothing is destroyed, the notice is set aside
            (§38 — "does a row disappear from the database?" — no).
          */}
          <IconButton
            size='small'
            aria-label='Dismiss suspicious activity notice'
            onClick={onDismiss}
            sx={{ color: 'text.secondary' }}
          >
            <X size={16} />
          </IconButton>
        </Stack>
      </Stack>

      {/* lineHeight 1.5 so descriptive copy doesn't read dense (§15).

          Two short lines at most. Figma's draft ran to four ("Review the activity
          tab below for detailed information on scans and their locations. Learn how
          to secure your product and prevent potential fraud.") — on a phone that is
          a wall of text at exactly the moment the reader wants to know what is wrong
          and where to look.

          The place is what makes it actionable: "An invalid scan was recorded." on
          its own leaves the reader nowhere to start, whereas a location gets an
          immediate "that isn't anywhere I've been". */}
      <Typography sx={{ fontSize: font.label, lineHeight: 1.5 }} color='text.secondary'>
        {place
          ? `An invalid scan was recorded at ${place}.`
          : 'An invalid scan was recorded.'}
      </Typography>

      {/* THE ACTION LIVES IN THE BLOCK, matching both sibling cards (2026-08-22 user
          directive, quoted in `MissingStateCallout`: "always temporary state buttons
          are in the block").

          This is a change of affordance from the Alert, which was tappable in its
          ENTIRETY — a whole-surface tap target was defensible when it stood alone,
          but it is not when it sits among sibling cards that are NOT tappable and
          hold their own buttons. Two cards in one stack obeying two different
          interaction models is worse than either model.

          SECONDARY, not primary: this navigates to evidence, it does not resolve
          anything. `MissingStateCallout`'s `Report found` is contained/primary
          precisely because it DOES resolve its state — same distinction that card
          already draws against the gift card's secondary `Cancel gift`.

          Copy is "Review scan" rather than "View on map" because the map cannot be
          promised: a scan with no coordinates is filtered out of the plotted set, so
          the Scans screen opens on the full map with nothing selected. See
          `MapHistory`'s `focusScanId`. */}
      <Box sx={{ mt: 1 }}>
        <Button
          fullWidth
          variant='outlined'
          startIcon={<MapPin size={18} />}
          onClick={onReviewScan}
          sx={{
            textTransform: 'none',
            py: 1.25,
            backgroundColor: theme => theme.palette.common.white,
            borderColor: theme => theme.palette.grey[300],
          }}
        >
          Review scan
        </Button>
      </Box>
    </Stack>
  </Box>
);
